import { colorBaseNeutralWhite } from '@duxton-web/design-tokens/themes/light';
import { ChevronDownUniversal, ChevronTopUniversal } from '@duxton-web/icons';
import { Button, Collapse, Flex, Typography } from '@duxton-web/ui';
import React, { useEffect, useMemo, useState } from 'react';

import paperImage from 'img/paper.svg';
import EmptyContentContainer from 'omega-rtc/components/form/ucd/common/EmptyContent';
import SmartCampaignFormContentCard from 'omega-rtc/components/form/ucd/SmartCampaignFormContentCard';
import {
  getRoundNumberFromSmartCommsRoundName,
  getSmartCommsRoundNameFromNumber,
} from 'omega-rtc/utils/ucd-utils';
import './SmartCampaignContentVariants.scss';

// @ts-ignore
import aiLoading from 'img/ai_loading.gif';
import { useCreateSmartCampaign } from 'omega-rtc/components/container/CreateSmartCampaignProvider';
import SmartCampaignSendPreview from 'omega-rtc/components/form/ucd/SmartCampaignSendPreview';

interface SmartCampaignFormContentContainerProps {
  language: string;
  isPreviewMode: boolean;
  isSelectedOnlyViewMode: boolean;
}

const SmartCampaignFormContentContainer: React.FC<SmartCampaignFormContentContainerProps> = ({
  language,
  isPreviewMode,
  isSelectedOnlyViewMode,
}) => {
  const [activeCollapseItems, setActiveCollapseItems] = useState([]);
  const [sentPreviewModalDetails, setSentPreviewModalDetails] = useState({
    isOpen: false,
    variants: undefined,
  });

  const {
    primaryLanguage,
    isMystiqueCallPending,
    primaryLanguageContentsInForm,
    isCurrentContentSourceManual,
  } = useCreateSmartCampaign();

  const roundNames = useMemo(() => {
    return Object.keys(primaryLanguageContentsInForm.rounds).reverse();
  }, [primaryLanguageContentsInForm]);

  useEffect(() => {
    // Set last round collapse to be open.
    if (primaryLanguageContentsInForm.lastRoundNumber > 0) {
      const latestRound = getSmartCommsRoundNameFromNumber(
        primaryLanguageContentsInForm.lastRoundNumber,
      );
      setActiveCollapseItems([latestRound, ...activeCollapseItems]);
    }
  }, [primaryLanguageContentsInForm]);

  const handleCollapseOnChange = (keys: string | string[]) => {
    setActiveCollapseItems(Array.isArray(keys) ? keys : [keys]);
  };

  const getAllVariantsOfARound = (roundName: string) => {
    return Object.keys(primaryLanguageContentsInForm.rounds[roundName])
      .map((variantName) => {
        const isChosen =
          primaryLanguageContentsInForm.rounds[roundName][variantName].isChosen;
        const isControlledGroup =
          primaryLanguageContentsInForm.selectedControlGroupVariant ===
          variantName;

        if (
          (primaryLanguage !== language || isSelectedOnlyViewMode) &&
          !isChosen &&
          !isControlledGroup
        ) {
          return undefined;
        }
        const slang =
          primaryLanguageContentsInForm.rounds[roundName][variantName].slang;

        return (
          <SmartCampaignFormContentCard
            sentPreviewMessage={({ variantName, message, title }) => {
              setSentPreviewModalDetails({
                isOpen: true,
                variants: { variantName, message, title },
              });
            }}
            isPreviewMode={isPreviewMode}
            slang={slang}
            language={language}
            isChosen={isChosen}
            roundName={roundName}
            variantName={variantName}
            isControlledGroup={isControlledGroup}
            key={`${roundName}-${variantName}-${language}`}
          />
        );
      })
      .filter((variant) => variant);
  };
  const contentContainerClassName = useMemo(() => {
    return `content-variants-container ${
      isPreviewMode
        ? 'content-variants-with-preview-container'
        : 'content-variants-without-preview-container'
    }`;
  }, [isPreviewMode]);

  useEffect(() => {
    /**
     * resizeObserver is throwing a error in development.
     * error: 'ResizeObserver loop completed with undelivered notifications.'
     * fix:
     */
    window.ResizeObserver = undefined;
  }, []);

  const collapseItems = roundNames.map((roundName) => ({
    key: roundName,
    forceRender: true,
    label: (
      <Typography.Title level={4}>
        {formatRoundName(roundName)}
      </Typography.Title>
    ),
    children: (
      <div className={contentContainerClassName}>
        {getAllVariantsOfARound(roundName)}
      </div>
    ),
  }));

  const expandIconOfCollapse = ({ isActive }: { isActive?: boolean }) => (
    <Button
      onClick={(e) => e.preventDefault()}
      icon={isActive ? <ChevronDownUniversal /> : <ChevronTopUniversal />}
      style={{
        width: 32,
        height: 32,
        backgroundColor: colorBaseNeutralWhite,
      }}
      type="text"
    />
  );

  const variantsOfAllRounds = useMemo(() => {
    return roundNames.flatMap((roundName) => getAllVariantsOfARound(roundName));
  }, [roundNames, isPreviewMode, isSelectedOnlyViewMode]);

  if (!variantsOfAllRounds.length) {
    return <EmptyContent />;
  }

  return (
    <React.Fragment>
      {primaryLanguage === language && !isCurrentContentSourceManual ? (
        <React.Fragment>
          {isMystiqueCallPending &&
            primaryLanguageContentsInForm.lastRoundNumber > 0 && (
              <Flex
                align="center"
                justify="center"
                style={{ padding: '32px 0px' }}
              >
                <img src={aiLoading} alt="loading..." width={60} height={60} />
              </Flex>
            )}
          <Collapse
            ghost
            items={collapseItems}
            expandIconPosition="end"
            activeKey={activeCollapseItems}
            onChange={handleCollapseOnChange}
            style={{
              padding: 0,
              width: '100%',
            }}
            expandIcon={expandIconOfCollapse}
          />
        </React.Fragment>
      ) : (
        <div className={contentContainerClassName}>{variantsOfAllRounds}</div>
      )}

      <SmartCampaignSendPreview
        open={sentPreviewModalDetails.isOpen}
        onClose={() => {
          setSentPreviewModalDetails({ isOpen: false, variants: undefined });
        }}
        selectedVariants={[sentPreviewModalDetails.variants]}
        language={language}
      />
    </React.Fragment>
  );
};

// Format round number in roundName to ordinal number.
const formatRoundName = (roundName: string): string | undefined => {
  if (!roundName) {
    return;
  }

  const roundNumber = getRoundNumberFromSmartCommsRoundName(roundName);
  const lastDigit = roundNumber % 10;
  const lastTwoDigits = roundNumber % 100;

  const getName = (suffixString: string) => {
    return `${roundNumber}${suffixString} round`;
  };

  // numbers ending with 11, 12, and 13 use 'th'.
  if (lastDigit === 1 && lastTwoDigits !== 11) {
    return getName('st');
  } else if (lastDigit === 2 && lastTwoDigits !== 12) {
    return getName('nd');
  } else if (lastDigit === 3 && lastTwoDigits !== 13) {
    return getName('rd');
  }

  return getName('th');
};

const EmptyContent = () => {
  return (
    <Flex
      align="center"
      justify="center"
      style={{ height: '60vh', margin: 'auto', width: 370 }}
    >
      <EmptyContentContainer
        image={paperImage}
        description={'No selected variant '}
        title={'Select two or more so you can easily compare them.'}
      />
    </Flex>
  );
};
export default SmartCampaignFormContentContainer;
